import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Typography, Tag, Tabs } from 'antd';
import { SwapRightOutlined } from '@ant-design/icons';
import { t } from '@/utils/translator';
import { getPreferredTimezone, formatDateRange } from '@/utils/dateFormatter';
import useAuthStore from '@store/useAuthStore';
import BasicLayout from '@/components/common/layout/basic';
import { useUserProjectsData } from '@/hooks/useUserProjectsData';
import i18n from '@/i18n';
import { ProjectStatusCode, ProjectStatusCodeColor } from '@constants/status';
import clsx from 'clsx';
const { Title } = Typography;

function CompanyEstimates() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userTimezone = getPreferredTimezone();

  const { loading, participationEstimates, clientProjects } = useUserProjectsData(user, isAuthenticated);

  function ParticipationProjects() {
    return (
      <>
        <Row gutter={[24, 24]} className="mb-5 pb-5">
          {!participationEstimates.length && (
            <Col xs={24}>
              <Card size="small" headStyle={{ fontSize: 18 }} className="border text-center rounded-2 p-4 bg-gray-1">
                <Title level={3} className="text-gray-7 mb-0">참여 신청한 프로젝트가 없습니다.</Title>
              </Card>
            </Col>
          )}
          {participationEstimates.length > 0 &&
            participationEstimates.map((Estimate) => (
              <Col xs={24} md={12} lg={12} xl={12} key={`participation-estimates-project-${Estimate.project.id}-company-${Estimate.company_id}-estimate-${Estimate.id}`}>
                <Card size="small" headStyle={{ fontSize: 18 }} bodyStyle={{ padding: 0 }} className="border rounded-2">
                  <Row>
                    <Col xs={24} lg={24}>
                      <div className={clsx('projects-card', Estimate.project.status_code === 'NOT_STARTED' && 'disabled', 'rounded-2')} key={`projects-${Estimate.project.id}-company-${Estimate.company_id}-estimates-${Estimate.id}`}>
                        <div className={clsx('border-bottom p-3', Estimate.project.status_code === 'NOT_STARTED' && 'bg-light')}>
                          <div className="mb-0">
                            <Title level={4} className="projects-card-title d-inline-block fw-medium mb-3" onClick={() => Estimate.project.status_code === 'NOT_STARTED' ? null : navigate(`/v2/projects/${Estimate.project.id}/estimates/${Estimate.id}/company/${Estimate.company_id}/estimates?project_code=${Estimate.project.project_code}`)} style={{ cursor: Estimate.project.status_code === 'NOT_STARTED' ? 'default' : 'pointer' }}>
                              {Estimate.project[`project_name_${i18n.language}`]}
                              <SwapRightOutlined />
                            </Title>
                          </div>
                          <div className="text-start" style={{ fontSize: '14px' }}>
                            {formatDateRange(Estimate.project.start_date, Estimate.project.end_date, userTimezone)}
                          </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center bg-light rounded-bottom-2 py-2 px-3">
                          <Tag color={ProjectStatusCodeColor[Estimate.project.status_code]} style={{ fontSize: '12px', lineHeight: 2.1 }}>
                            {ProjectStatusCode[Estimate.project.status_code]}
                          </Tag>
                          <p className="mb-0" style={{ fontSize: '14px' }}>{Estimate.company.company_name}</p>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            ))}
        </Row>
      </>
    );
  }

  function ClientProject() {
    return (
      <Row gutter={[24, 24]} className="mb-5 pb-5">
        {!clientProjects.length && (
          <Col xs={24}>
            <Card size="small" headStyle={{ fontSize: 18 }} className="border text-center rounded-2 p-4 bg-gray-1">
              <Title level={3} className="text-gray-7 mb-0">주관 프로젝트가 없습니다.</Title>
            </Card>
          </Col>
        )}
        {clientProjects.length > 0 &&
          clientProjects.map((Project) => (
            <Col xs={24} md={12} key={`client-project-${Project.project_id}-${Project.project_code}`}>
              <Card size="small" headStyle={{ fontSize: 18 }} bodyStyle={{ padding: 0 }} className="border rounded-2">
                <div className={clsx('projects-card', Project.status_code === 'NOT_STARTED' && 'disabled', 'rounded-2')}>
                  <div className={clsx('border-bottom p-3', Project.status_code === 'NOT_STARTED' && 'bg-light')}>
                    <div className="mb-0">
                      <Title level={4} className="projects-card-title d-inline-block fw-medium mb-3" onClick={() => Project.status_code === 'NOT_STARTED' ? null : navigate(`/client/dashboard?project_code=${Project.project_code}`)} style={{ cursor: Project.status_code === 'NOT_STARTED' ? 'default' : 'pointer' }}>
                        {Project[`project_name_${i18n.language}`]}
                        <SwapRightOutlined />
                      </Title>
                    </div>
                    <div className="text-start" style={{ fontSize: '14px' }}>
                      {formatDateRange(Project.start_date, Project.end_date, userTimezone)}
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center bg-light rounded-bottom-2 py-2 px-3">
                    <Tag color={ProjectStatusCodeColor[Project.status_code]} style={{ fontSize: '12px', lineHeight: 2.1 }}>
                      {ProjectStatusCode[Project.status_code]}
                    </Tag>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
      </Row>
    );
  }

  function ClientParticipationProjestPanel() {
    const [ActiveProjectTabkey, setActiveProjectTabkey] = useState('project-tab-client-project');
    return (
      <Tabs
        activeKey={ActiveProjectTabkey}
        onChange={setActiveProjectTabkey}
        tabBarStyle={{}}
        items={[
          { label: <span className="fs-6">주관 프로젝트</span>, key: 'project-tab-client-project', children: <ClientProject /> },
          { label: <span className="fs-6">참여 프로젝트</span>, key: 'project-tab-participation-project', children: <ParticipationProjects /> },
        ]}
      ></Tabs>
    );
  }

  return (
    <>
      <BasicLayout body_classname="bg-white">
        <Row gutter={[24, 24]}>
          <Col xs={24} md={{ span: 18, offset: 3 }}>
            <Row gutter={[24, 24]} className="mb-4">
              <Col xs={24}>
                <Title level={2} className="fw-medium mb-0">{t('프로젝트 선택하기')}</Title>
              </Col>
            </Row>
            {loading && <div>프로젝트 불러오는 중...</div>}
            {!loading && clientProjects.length > 0 && participationEstimates.length > 0 && <ClientParticipationProjestPanel />}
            {!loading && participationEstimates.length > 0 && !clientProjects.length && <ParticipationProjects />}
            {!loading && !participationEstimates.length && clientProjects.length > 0 && <ClientProject />}
          </Col>
        </Row>
      </BasicLayout>
    </>
  );
}

export default CompanyEstimates;